import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
  HStack,
  Button,
  Checkbox,
  Box,
  Icon,
} from "@chakra-ui/react";
import PrimaryButton from "src/components/PrimaryButton";
import { post, BASE_URL } from "src/api";
import { useIntl } from "react-intl";
import SharedWithUsersList from "./SharedWithUsersList";
import MultiEmailInput from "./MultiEmailInput";
import SharingResultsModal from "./SharingResultsModal";
import { FaUsers } from "react-icons/fa";
import SharingToAllResultsModal from "./shareToAllResultsModal";

function SharingModal({ isOpen, onClose, sharedEntity, showOptions = true }) {
  const intl = useIntl();
  const toast = useToast();
  const [emails, setEmails] = useState<string[]>([]);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSharingToAllCustomers, setIsSharingToAllCustomers] = useState(false);
  const [sharingResult, setSharingResult] = useState<any>(null);
  const [sharingToAllResult, setSharingToAllResult] = useState<any>(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isShareToAllResultsModalOpen, setIsShareToAllResultsModalOpen] =
    useState(false);
  const [shareToAllCustomers, setShareToAllCustomers] = useState(false);
  const [permission, setPermission] = useState<"read" | "update">("read");
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
  const [sharedByAdminCount, setSharedByAdminCount] = useState<
    number | undefined
  >();
  const [sharedByAdminTimestamp, setSharedByAdminTimestamp] = useState<
    number | undefined
  >();
  const [acceptanceRate, setAcceptanceRate] = useState<number | undefined>();

  const isAdmin = localStorage.getItem("isAdmin") === "true";

  const fetchItems = useCallback(() => {
    setLoadingUsers(true);
    setSharedByAdminCount(undefined);
    setSharedByAdminTimestamp(undefined);
    setAcceptanceRate(undefined);

    fetch(
      `${BASE_URL}/shared/shared-with/${sharedEntity.name}/${sharedEntity.id}`,
      {
        headers: {
          authorization: `Bearer ${window.localStorage.getItem("__sal_auth")}`,
          "x-localization": localStorage.getItem("locale") || "",
          origin: window.location.origin,
          "Content-Type": "application/json",
        },
      }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const lastSharedByAdminCount = data.lastSharedByAdminToAllCount;
        const lastSharedByAdminTimestamp = data.lastSharedByAdminToAllTimestamp;
        const currentIntentionAcceptanceRate = data.acceptanceRate;

        Boolean(lastSharedByAdminCount) &&
          setSharedByAdminCount(+lastSharedByAdminCount);
        Boolean(lastSharedByAdminTimestamp) &&
          setSharedByAdminTimestamp(+lastSharedByAdminTimestamp);
        Boolean(currentIntentionAcceptanceRate) &&
          setAcceptanceRate(+currentIntentionAcceptanceRate.toFixed(2));

        setLoadingUsers(false);
        if (
          data &&
          Array.isArray(data.sharedItems) &&
          data.sharedItems.length
        ) {
          setUsersList(data.sharedItems);
        }
      })
      .catch((error) => {
        setLoadingUsers(false);
        // Handle error silently
      });
  }, [sharedEntity.id, sharedEntity.name]);

  useEffect(() => {
    fetchItems();
    return () => {
      setEmails([]);
      setUsersList([]);
      setLoadingUsers(false);
    };
  }, [fetchItems, sharedEntity.id, sharedEntity.name, isOpen]);

  const handleShare = async (emails: string[]) => {
    interface Result {
      validAccessorEmails: string[]; // emails that are valid and not already shared
      invalidAccessorEmails: string[]; // emails that are not found in the database
      alreadySharedAccessorEmails: string[]; // emails that are already shared
      newShareCount: number; // number of emails that are valid and not already shared
      statusCode?: number;
      message?: string;
    }

    if (emails.length === 0) return;

    setIsSubmitting(true);

    try {
      const response = await post("/shared/bulk", {
        accessorEmails: emails,
        entityName: sharedEntity?.name,
        entityId: Number(sharedEntity?.id),
        permission,
      });

      const result = response as unknown as Result;

      // Case 1: No valid users found for sharing
      if (
        result.statusCode === 404 &&
        result.message === "No valid users found for sharing"
      ) {
        toast({
          title: intl.formatMessage({ id: "emailNotFound" }),
          status: "error",
          isClosable: true,
        });

        // Always refresh the shared list and clear emails
        fetchItems();
        setEmails([]);

        // Always close the sharing modal
        return onClose();
      }

      // Case 2: Single email - always use toast
      if (emails.length === 1) {
        const email = emails[0];
        let toastMessage = "";
        let toastStatus: "success" | "warning" | "error" = "success";

        if (result.validAccessorEmails.includes(email)) {
          toastMessage =
            intl.formatMessage({ id: "successfullySharedWith" }) + ` ${email}`;
          toastStatus = "success";
        } else if (result.alreadySharedAccessorEmails.includes(email)) {
          toastMessage = `${email} ${intl.formatMessage({
            id: "alreadyHasAccess",
          })}`;
          toastStatus = "warning";
        }

        toast({
          title: toastMessage,
          status: toastStatus,
          isClosable: true,
        });
      }
      // Case 3: Multiple emails - check if all successful
      else if (result.validAccessorEmails.length === emails.length) {
        toast({
          title: intl.formatMessage({ id: "successfullySharedWithAll" }),
          status: "success",
          isClosable: true,
        });

        // Case 4: Multiple emails - check if all are previously shared
      } else if (result.alreadySharedAccessorEmails.length === emails.length) {
        toast({
          title: intl.formatMessage({ id: "alreadySharedWithAll" }),
          status: "warning",
          isClosable: true,
        });
      }
      // Case 5: Multiple emails with mixed results - show detailed modal
      else {
        setSharingResult(result);
        setIsResultsModalOpen(true);
      }

      // Always refresh the shared list and clear emails
      fetchItems();
      setEmails([]);

      // Always close the sharing modal
      onClose();
    } catch (error) {
      toast({
        title: intl.formatMessage({ id: "wentWrong" }),
        status: "error",
        isClosable: true,
      });
      // Close modal even on error
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShareToAllCustomers = async () => {
    setIsSharingToAllCustomers(true);
    // alert("share to all customers");

    const resetStates = () => {
      setIsSharingToAllCustomers(false);
      setShareToAllCustomers(false);
      onClose();
    };

    try {
      const requestBody = {
        entityName: sharedEntity?.name,
        entityId: Number(sharedEntity?.id),
        permission,
      };

      const response = (await post(
        "/admin/shared/to-all-active-users",
        requestBody
      )) as unknown as {
        newSharesCount: number;
        alreadySharedCount: number;
      };

      if (response) {
        if (response.newSharesCount > 0) {
          if (response.alreadySharedCount > 0) {
            setSharingToAllResult({
              newSharesCount: response.newSharesCount.toString(),
              alreadySharedCount: response.alreadySharedCount.toString(),
            });
            setIsShareToAllResultsModalOpen(true);
          } else {
            toast({
              title: intl.formatMessage({ id: "successfullySharedWithAll" }),
              status: "success",
              isClosable: true,
            });
          }
        } else {
          toast({
            title: intl.formatMessage({ id: "alreadySharedWithAll" }),
            status: "warning",
            isClosable: true,
          });
        }
        onClose();
      } else {
        toast({
          title: intl.formatMessage({ id: "wentWrong" }),
          status: "error",
          isClosable: true,
        });
      }
      // Close modal even on error

      return resetStates();
    } catch (error) {
      toast({
        title: intl.formatMessage({ id: "wentWrong" }),
        status: "error",
        isClosable: true,
      });
      // Close modal even on error
      return resetStates();
    }
  };

  const handlePermissionChange = (value: string) => {
    setPermission(value as "read" | "update");
  };

  const handleResultsModalClose = () => {
    setIsResultsModalOpen(false);
    setSharingResult(null);
  };

  const handleToAllResultsModalClose = () => {
    setIsShareToAllResultsModalOpen(false);
    setSharingToAllResult(null);
  };

  return (
    <>
      <Modal isCentered size={"xs"} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        {/* @ts-ignore */}
        <ModalContent borderRadius="30px" maxW="550px" p="10px">
          <ModalCloseButton />
          <ModalBody>
            <Text
              display="inline-block"
              color="brand.900"
              fontSize="18px"
              fontWeight="bold"
              fontFamily="Calibri (Body)"
              mb="4"
            >
              {intl.formatMessage({ id: "shareWith" })}
            </Text>

            <Text fontSize="14px" color="gray.600" mb="2">
              {intl.formatMessage({ id: "sendToOneOrMore" })}
            </Text>

            <MultiEmailInput
              emails={emails}
              onEmailsChange={setEmails}
              placeholder={intl.formatMessage({ id: "email" })}
              isDisabled={shareToAllCustomers}
            />
            {isAdmin && (
              <Box
                bg="blue.50"
                borderRadius="md"
                p="3"
                mb="4"
                mt="4"
                border="1px solid"
                borderColor="blue.100"
              >
                <Checkbox
                  isChecked={shareToAllCustomers}
                  onChange={(e) => setShareToAllCustomers(e.target.checked)}
                  colorScheme="blue"
                  size="md"
                >
                  <HStack spacing={2} align="center">
                    <Icon as={FaUsers} color="blue.500" />
                    <Text fontSize="14px" fontWeight="medium">
                      {intl.formatMessage({ id: "shareToAllCustomers" })}
                    </Text>
                  </HStack>
                </Checkbox>
              </Box>
            )}
            {showOptions ? (
              <RadioGroup
                onChange={handlePermissionChange}
                defaultValue={permission}
                mt="4"
              >
                <Stack direction="row">
                  <Radio value="read">
                    {intl.formatMessage({ id: "readOnly" })}
                  </Radio>
                  <Radio value="update">
                    {intl.formatMessage({ id: "readAndUpdate" })}
                  </Radio>
                </Stack>
              </RadioGroup>
            ) : null}

            <HStack spacing={3} my={"32px"} justify="center">
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {intl.formatMessage({ id: "close" })}
              </Button>
              <PrimaryButton
                onClick={() => {
                  if (shareToAllCustomers) {
                    handleShareToAllCustomers();
                  } else {
                    handleShare(emails);
                  }
                }}
                disabled={
                  (!shareToAllCustomers && emails.length === 0) ||
                  isSubmitting ||
                  isSharingToAllCustomers
                }
                isLoading={isSubmitting || isSharingToAllCustomers}
                loadingText={
                  shareToAllCustomers
                    ? intl.formatMessage({ id: "sharing_to_all_customers" })
                    : intl.formatMessage({ id: "sharing" })
                }
                width={isSharingToAllCustomers ? "fit-content" : "168px"}
              >
                {intl.formatMessage({ id: "share" })}
              </PrimaryButton>
            </HStack>

            {usersList.length ||
            loadingUsers ||
            (sharedByAdminCount && sharedByAdminTimestamp && acceptanceRate !== undefined) ? (
              <SharedWithUsersList
                sharedList={usersList}
                setUsersList={setUsersList}
                showOptions={showOptions}
                loadingUsers={loadingUsers}
                sharedByAdminCount={sharedByAdminCount}
                sharedByAdminTimestamp={sharedByAdminTimestamp}
                acceptanceRate={acceptanceRate}
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Sharing Results Modal for multiple emails with mixed results */}
      {sharingResult && (
        <SharingResultsModal
          isOpen={isResultsModalOpen}
          onClose={handleResultsModalClose}
          result={sharingResult}
        />
      )}

      {sharingToAllResult && (
        <SharingToAllResultsModal
          isOpen={isShareToAllResultsModalOpen}
          onClose={handleToAllResultsModalClose}
          result={sharingToAllResult}
        />
      )}
    </>
  );
}

export default SharingModal;
